import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Typography, Button, Table, Input, Select, Spin } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import _get from "lodash/get";

import { getCandidateListRequest } from "providers/CandidateProvider/slice";
import { getActiveContestsRequest } from "providers/ContestProvider/slice";
import CandidateColumn from "components/Columns/CandidateColumn";
import "./styles.less";

const { Search } = Input;
const { Option } = Select;

const CandidateList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedContestId, setSelectedContestId] = useState("");

  const candidateList = useSelector((state) => state.candidateSlice.candidateList);
  const pagination = useSelector((state) => state.candidateSlice.pagination);
  const isLoading = useSelector((state) => state.candidateSlice.isLoading);
  const activeContests = useSelector((state) => state.contestSlice.activeContests || []);
  const contestLoading = useSelector((state) => state.contestSlice.isLoading);

  // Fetch contests for the select box
  useEffect(() => {
    dispatch(getActiveContestsRequest());
  }, [dispatch]);

  // Fetch all candidates or by contest
  const loadCandidateList = useCallback(
    (params = {}) => {
      const requestParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        keyword: params.keyword !== undefined ? params.keyword : searchKeyword,
        sortByRanking: true,
      };
      if (params.contestId) {
        requestParams.contestId = params.contestId;
      } else if (selectedContestId) {
        requestParams.contestId = selectedContestId;
      }
      dispatch(getCandidateListRequest(requestParams));
    },
    [searchKeyword, selectedContestId, dispatch]
  );

  useEffect(() => {
    loadCandidateList({});
  }, [selectedContestId, loadCandidateList]);

  const handleTableChange = (tablePagination, filters, sorter) => {
    loadCandidateList({
      page: tablePagination.current,
      limit: tablePagination.pageSize,
    });
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    loadCandidateList({ page: 1, keyword: value });
  };

  const handleContestChange = (value) => {
    setSelectedContestId(value);
    loadCandidateList({ contestId: value, page: 1 });
  };

  const columns = CandidateColumn({
    onRefresh: () => loadCandidateList(),
    pagination: pagination,
  });

  return (
    <div className="candidate-list-wrapper">
      <Row className="candidate-list-header" justify="space-between" align="middle">
        <Row align="middle">
          <Typography.Title level={4}>Danh sách thí sinh</Typography.Title>
        </Row>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          style={{ width: 150 }}
          onClick={() => navigate("/voting/candidate_create")}
        >
          Thêm thí sinh
        </Button>
      </Row>
      <div className="candidate-list-content">
        <Row className="candidate-list-filters" gutter={16} align="middle">
          <div className="filter-item">
            <Search
              placeholder="Tìm kiếm thí sinh..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
          </div>
          <div className="filter-item">
            <Select
              allowClear
              showSearch
              placeholder="Chọn cuộc thi (tùy chọn)"
              style={{ width: 250 }}
              loading={contestLoading}
              value={selectedContestId || undefined}
              onChange={handleContestChange}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {activeContests.map((contest) => (
                <Option key={contest._id} value={contest._id}>
                  {contest.name}
                </Option>
              ))}
            </Select>
          </div>
        </Row>
        <Table
          columns={columns}
          dataSource={candidateList}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} thí sinh`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          locale={{ emptyText: "Không có thí sinh nào." }}
        />
      </div>
    </div>
  );
};

export default CandidateList;
